import { useMutation } from '@apollo/client';
import { capitalizeFirstLetter } from 'formatters';
import { useNotifications } from 'modules/notification';

import { equipmentImagesApi } from './index';
import {
  DeleteEquipmentAssetResponse,
  DeleteEquipmentAssetVariables,
  UploadEquipmentAssetsResponse,
  UploadEquipmentAssetsVariables,
} from './types';

export function useEquipmentAssets() {
  const { showNotification } = useNotifications();

  const [onUpload, { loading: isAssetsUploading }] = useMutation<
    UploadEquipmentAssetsResponse,
    UploadEquipmentAssetsVariables
  >(equipmentImagesApi.uploadAssets);

  const handleUploadImages = async (equipmentId: string, type: 'IMAGE', files: File[]) => {
    try {
      const { data: response } = await onUpload({
        variables: {
          input: {
            type,
            assets: files,
            entityId: equipmentId,
          },
        },
      });

      if (response?.equipment?.result?.error) {
        showNotification(response?.equipment?.result?.error ?? 'Something went wrong', { type: 'error' });
        return;
      }

      showNotification(`${capitalizeFirstLetter(type)} successfully uploaded!`);
    } catch (error: any) {
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  const [onDelete, { loading: isDeleting }] = useMutation<DeleteEquipmentAssetResponse, DeleteEquipmentAssetVariables>(
    equipmentImagesApi.removeAssets,
    { refetchQueries: [equipmentImagesApi.fetchAssets], awaitRefetchQueries: true }
  );

  const handleDelete = async (id: string) => {
    try {
      const { data: response } = await onDelete({
        variables: {
          input: { id },
        },
      });

      if (response?.equipment?.result?.error) {
        showNotification(response?.equipment?.result?.error ?? 'Something went wrong', { type: 'error' });
        return;
      }

      showNotification('Image successfully deleted!');
    } catch (error: any) {
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  return {
    isDeleting,
    handleDelete,
    isAssetsUploading,
    handleUploadImages,
  };
}
